/*
 * 浏览器导出管理器：管理隐藏iframe里的export-runner（独立eu-core实例+WebCodecs编码）。
 * API:
 *   exportRunner.isSupported()          -> boolean  当前浏览器能否视频导出(WebCodecs)
 *   exportRunner.exportVideo(cfg, opts, onProgress) -> Promise<{blob, ext, totalMs, frames}>
 *   exportRunner.exportImage(cfg, opts, onProgress) -> Promise<{blob, ext}>
 *   exportRunner.cancel()               -> void
 * cfg:  {movieId, apiUrl, cdnUrl, token, pixel, subtitle, transparent, width, height, script}
 *       script=编辑器活剧本encodeToString串（含未保存改动），为空则导服务器已存版本
 * opts(video): {format:'mp4'|'webm', fps, bitrate, audio}
 * opts(image): {page, step} 单步步尾帧 | {all:true} 全片步尾打包zip | {steps:[{page,step}]}
 *              format:'png'|'jpeg', quality
 * onProgress(phase, ratio, detail): phase = 'init'|'load'|'render'|'encode'|'audio'
 */
const RUNNER_URL = '/static/eucore/export-runner.html'

let iframe = null
let iframeReady = false
let loadedResolve = null
let job = null        // {resolve, reject, onProgress}
let initSeq = null    // {config, resolve, reject}
let runnerInited = false // runner上下文已跑过init：全局Res缓存/监听器不可安全复用

function ensureIframe () {
  if (iframe) return iframe
  iframe = document.createElement('iframe')
  iframe.style.cssText = 'position:fixed;left:-10000px;top:0;width:900px;height:560px;border:0;visibility:hidden;'
  iframe.src = RUNNER_URL
  document.body.appendChild(iframe)
  return iframe
}

function postToRunner (msg) {
  if (iframe && iframe.contentWindow) {
    iframe.contentWindow.postMessage(Object.assign({ source: 'eucore-export-parent' }, msg), '*')
  }
}

window.addEventListener('message', (e) => {
  const d = e.data
  if (!d || d.source !== 'eucore-export') return
  if (d.type === 'loaded') {
    iframeReady = true
    if (loadedResolve) { const r = loadedResolve; loadedResolve = null; r() }
    return
  }
  if (d.type === 'ready') {
    if (initSeq) { const s = initSeq; initSeq = null; s.resolve(d) }
    return
  }
  if (d.type === 'progress') {
    if (job && job.onProgress) job.onProgress(d.phase, d.ratio, d.detail)
    return
  }
  if (d.type === 'done') {
    if (job) { const j = job; job = null; j.resolve(d) }
    return
  }
  if (d.type === 'cancelled') {
    if (job) { const j = job; job = null; j.reject(new Error('cancelled')) }
    return
  }
  if (d.type === 'error') {
    const err = new Error(d.message || 'export error')
    if (initSeq) { const s = initSeq; initSeq = null; s.reject(err); return }
    if (job) { const j = job; job = null; j.reject(err) }
  }
})

function waitLoaded () {
  ensureIframe()
  if (iframeReady) return Promise.resolve()
  return new Promise((resolve) => { loadedResolve = resolve })
}

function initSession (config, onProgress) {
  if (initSeq) return Promise.reject(new Error('init in flight'))
  // 二次init时旧runner上下文残留（Res缓存的BundleAssets/脚本、挂接的监听器）
  // 会在重载素材时崩（removeChildren->null.remove），重载iframe拿干净上下文
  if (runnerInited && iframe && iframe.contentWindow) {
    runnerInited = false
    iframeReady = false
    job = null
    iframe.contentWindow.location.reload()
  }
  return waitLoaded().then(() => {
    return new Promise((resolve, reject) => {
      initSeq = { resolve, reject }
      runnerInited = true
      if (onProgress) onProgress('init', 0, '')
      postToRunner({ type: 'init', config: config })
      // 超时兜底：资源加载不应静默挂死
      setTimeout(() => {
        if (initSeq) { initSeq = null; reject(new Error('导出环境初始化超时')) }
      }, 120000)
    })
  })
}

function sendJob (type, options, onProgress) {
  return new Promise((resolve, reject) => {
    job = { resolve, reject, onProgress }
    postToRunner({ type: type, options: options })
  })
}

const exportRunner = {
  isSupported () {
    return typeof VideoEncoder !== 'undefined' && typeof VideoFrame !== 'undefined'
  },

  /**
   * 只初始化runner并预加载 -> {width, height, totalMs, pages:[{durationMs,stepCount}]}
   * 图片导出需先prepare拿页/步列表，再runImage
   */
  prepare (config, onProgress) {
    if (job || initSeq) return Promise.reject(new Error('已有导出任务进行中'))
    return initSession(config, onProgress)
  },

  /**
   * prepare之后发送任务（runner已就绪时直接用）
   */
  runVideo (options, onProgress) {
    if (job) return Promise.reject(new Error('已有导出任务进行中'))
    return sendJob('video', options || {}, (phase, ratio, detail) => {
      if (onProgress) onProgress(phase, ratio, detail)
    })
  },

  runImage (options, onProgress) {
    if (job) return Promise.reject(new Error('已有导出任务进行中'))
    return sendJob('image', options || {}, (phase, ratio, detail) => {
      if (onProgress) onProgress(phase, ratio, detail)
    })
  },

  /**
   * 一步到位：导出整片视频 -> {blob, ext:'mp4'|'webm', totalMs, frames}
   */
  exportVideo (config, options, onProgress) {
    return this.prepare(config, onProgress).then(() => {
      return this.runVideo(options, onProgress)
    })
  },

  /**
   * 一步到位：导出步尾静帧 -> {blob, ext:'png'|'jpg'|'zip'}
   */
  exportImage (config, options, onProgress) {
    return this.prepare(config, onProgress).then(() => {
      return this.runImage(options, onProgress)
    })
  },

  cancel () {
    postToRunner({ type: 'cancel' })
  },

  /** 销毁runner释放资源 */
  destroy () {
    if (iframe && iframe.parentNode) iframe.parentNode.removeChild(iframe)
    iframe = null
    iframeReady = false
    runnerInited = false
    job = null
    initSeq = null
  }
}

export default exportRunner



// WEBPACK FOOTER //
// ./src/common/scripts/exportRunner.js